import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Row,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    ClearOutlined,
    InfoCircleOutlined,
    BulbOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface OrbitalAIConfigRow {
    id?: number;
    expert_type: string;
    system_prompt: string;
    user_prompt: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface OrbitalAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        orbital_ai_config?: OrbitalAIConfigRow[];
    };
}

interface ExpertMeta {
    name: string;
    icon: React.ReactNode;
    color: string;
    description: string;
}

const ORBITAL_EXPERT_META: Record<string, ExpertMeta> = {
    orbital_findings: {
        name: 'Findings',
        icon: <BulbOutlined />,
        color: '#e07a5f',
        description:
            'Three short headline-style findings extracted from the brand analysis (8–12 words each, no full sentences). Surfaced on the dashboard as "Live AI Hot Take: Notable Findings".',
    },
    orbital_actions: {
        name: 'Actions',
        icon: <ThunderboltOutlined />,
        color: '#3b82f6',
        description:
            'Three directive-style actions (8–14 words each, each starting with a verb). Surfaced on the dashboard alongside the findings.',
    },
};

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for Orbital AI.
 *
 * Vertical sidebar tabs — one tab per orbital_ai_config row, labelled with
 * the friendly name + icon. Operational toggles, provider/fallback, and the
 * sticky-bad-cache warning live above the tabs because they apply across
 * the whole subsystem.
 */
export const OrbitalAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<OrbitalAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [isClearingCache, setIsClearingCache] = useState(false);

    const clearCache = async () => {
        if (!jwt) return;
        setIsClearingCache(true);
        try {
            const result = await aiAdminApi.clearCache(jwt, 'orbital_ai');
            message.success(
                `Cleared ${result.rowsCleared} row(s) from ${result.tables.join(', ')}.`,
            );
        } catch (err: any) {
            message.error(err.message || 'Failed to clear cache');
        } finally {
            setIsClearingCache(false);
        }
    };

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.orbital_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const updateRow = (expertType: string, patch: Partial<OrbitalAIConfigRow>) => {
        editor.setPayload((prev) => {
            const next = (prev!.configTableRows.orbital_ai_config ?? []).map((r) =>
                r.expert_type === expertType ? { ...r, ...patch } : r,
            );
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, orbital_ai_config: next },
            };
        });
    };

    const primaryValue: ProviderModelTempValue = {
        provider: (settings.ORBITAL_AI_PRIMARY_PROVIDER as ProviderId) || guessProvider(settings.ORBITAL_AI_PRIMARY_MODEL),
        model: settings.ORBITAL_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.ORBITAL_AI_PRIMARY_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.ORBITAL_AI_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.ORBITAL_AI_FALLBACK_MODEL),
        model: settings.ORBITAL_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.ORBITAL_AI_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            ORBITAL_AI_PRIMARY_PROVIDER: next.provider,
            ORBITAL_AI_PRIMARY_MODEL: next.model,
            ORBITAL_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            ORBITAL_AI_FALLBACK_PROVIDER: next.provider,
            ORBITAL_AI_FALLBACK_MODEL: next.model,
            ORBITAL_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    const orbitalEnabled = (settings.ORBITAL_AI_ENABLED ?? 'false').toString().toLowerCase() === 'true';
    const activeCount = rows.filter((r) => isActiveBool(r.is_active)).length;

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Orbital narrative synthesis. Consumes Advice AI output and weaves it into the
                    orbital story.{' '}
                    <strong>{activeCount}</strong> of <strong>{rows.length}</strong> sections
                    active.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Bad LLM responses can get sticky"
                description={
                    <>
                        The orbital response cache is keyed by{' '}
                        <code>(organisationId, brandId, expertType, inputHash)</code> — not by
                        prompt content. Publishing this subsystem clears{' '}
                        <code>orbital_ai_response</code>, but if the very next LLM call returns a
                        short / malformed array, <code>parseJsonArray</code> pads the missing
                        slots with the sentinel <code>"Insight pending"</code> and that padded
                        result gets saved into the cache. Subsequent dashboard refreshes will
                        keep returning the bad cached value until the cache is cleared again —
                        the LLM is not re-called. Click <strong>Clear orbital cache</strong> to
                        delete every row in <code>orbital_ai_response</code> and force the next
                        dashboard load to re-call the LLM.
                    </>
                }
                action={
                    <Button
                        danger
                        icon={<ClearOutlined />}
                        loading={isClearingCache}
                        onClick={clearCache}
                    >
                        Clear orbital cache
                    </Button>
                }
            />

            <Card title="Operational toggles" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Orbital AI enabled</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>ORBITAL_AI_ENABLED</code>
                            </Text>
                            <Switch
                                checked={orbitalEnabled}
                                onChange={(checked) => setSetting('ORBITAL_AI_ENABLED', checked ? 'true' : 'false')}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            {rows.length === 0 ? (
                <Empty description="No orbital_ai_config rows — has the v1 baseline been seeded?" />
            ) : (
                <Card>
                    <Tabs
                        tabPosition="left"
                        style={{ minHeight: 500 }}
                        items={rows.map((row) => {
                            const meta = ORBITAL_EXPERT_META[row.expert_type];
                            const isActive = isActiveBool(row.is_active);
                            return {
                                key: row.expert_type,
                                label: (
                                    <span>
                                        {isActive ? (
                                            <Badge dot status="success" offset={[-4, 0]}>
                                                <span>
                                                    <span style={{ color: meta?.color, marginRight: 8 }}>
                                                        {meta?.icon}
                                                    </span>
                                                    {meta?.name ?? row.expert_type}
                                                </span>
                                            </Badge>
                                        ) : (
                                            <span>
                                                <span style={{ color: meta?.color, marginRight: 8, opacity: 0.5 }}>
                                                    {meta?.icon}
                                                </span>
                                                <span style={{ opacity: 0.6 }}>
                                                    {meta?.name ?? row.expert_type}
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <OrbitalRowEditor
                                        row={row}
                                        meta={meta}
                                        onChange={(patch) => updateRow(row.expert_type, patch)}
                                    />
                                ),
                            };
                        })}
                    />
                </Card>
            )}
        </div>
    );
};

interface RowEditorProps {
    row: OrbitalAIConfigRow;
    meta: ExpertMeta | undefined;
    onChange: (patch: Partial<OrbitalAIConfigRow>) => void;
}

const OrbitalRowEditor: React.FC<RowEditorProps> = ({ row, meta, onChange }) => {
    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message={<strong>{meta?.name ?? row.expert_type}</strong>}
                description={meta?.description ?? `expert_type: ${row.expert_type}`}
            />

            <Space style={{ marginBottom: 16 }} wrap>
                <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                    {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                </Tag>
                <Switch
                    checked={isActiveBool(row.is_active)}
                    onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                    checkedChildren="on"
                    unCheckedChildren="off"
                />
            </Space>

            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                <code>system_prompt</code> sets the role/persona,{' '}
                <code>user_prompt</code> is the per-call template. Use the{' '}
                <code>{'{input_text}'}</code> placeholder in the user prompt to inject the
                source Advice AI text — <code>OrbitalAIService</code> substitutes it at call
                time.
            </Paragraph>

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="orbital_ai"
                    label="System / persona prompt — system_prompt"
                    value={row.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                />
            </div>
            <PromptEditor
                consumerSubsystem="orbital_ai"
                label="User-message template — user_prompt"
                value={row.user_prompt ?? ''}
                onChange={(v) => onChange({ user_prompt: v })}
                rows={14}
            />

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginTop: 16 }}
                message="Available placeholders"
                description={
                    <span>
                        <code>{'{input_text}'}</code> — the source Advice AI output text
                        (concatenated when there are multiple advice rows for the brand)
                    </span>
                }
            />
        </div>
    );
};
